import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { generateSharePassword, SHARE_DEFAULT_END_DAYS, SHARE_PASSWORD_MIN, type ShareTheme } from '@zerogap/shared';
import { get, patch, post, qs } from '../api';
import { arrow, locale, msg, tr } from '../i18n';
import { useTenant } from '../session';
import {
  ago, Badge, bytes, Button, Card, ConfirmDialog, CopyBox, Empty, ErrorBox, Field, friendlyError, Icons, Loading, Modal, PageHead, Segmented,
  TechDetails, useToast, when,
} from '../ui';

/**
 * Shared links (docs/sharing.md): a link anyone can open, without an account, to see and download chosen
 * files. Everything here is in plain words: who can open it, until when, and who did.
 */

export interface ShareSource {
  recoveryPointId?: string;
  folderArchiveId?: string;
}

interface Estimate {
  files: number;
  dirs: number;
  bytes: number;
  pictures: number;
  filesRemovedAt: string | null;
  passwordRequired: boolean;
  sharingEnabled: boolean;
  maxBytes: number;
  maxFiles: number;
}

export interface ShareView {
  id: string;
  name: string;
  theme: ShareTheme;
  status: 'active' | 'stopped' | 'ended';
  endReason: string | null;
  deviceId: string;
  deviceName: string | null;
  recoveryPointId: string | null;
  backupTime: string | null;
  folderArchiveId: string | null;
  archivePath: string | null;
  createdBy: string | null;
  createdAt: string;
  expiresAt: string | null;
  stoppedAt: string | null;
  endedAt: string | null;
  hasPassword: boolean;
  files: number;
  bytes: number;
  opens: number;
  downloads: number;
  lastOpenedAt: string | null;
  previews: string;
}

type EndChoice = '1' | '7' | '30' | 'date' | 'never';

const dateOnly = (iso: string) => new Date(iso).toLocaleDateString(locale(), { day: 'numeric', month: 'long', year: 'numeric' });
const tomorrow = () => new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);
/** The end of the chosen day, in the person's own time zone. */
const endOfDay = (d: string) => new Date(`${d}T23:59:00`).toISOString();
const newPassword = () => generateSharePassword((n) => crypto.getRandomValues(new Uint8Array(n)));
const lastName = (p: string) => p.slice(p.lastIndexOf('/') + 1) || p;

function endOf(choice: EndChoice, date: string): { endsInDays?: number; expiresAt?: string | null } {
  if (choice === 'never') return { expiresAt: null };
  if (choice === 'date') return { expiresAt: endOfDay(date) };
  return { endsInDays: Number(choice) };
}

const END_OPTIONS = (allowNever: boolean) => [
  { key: '1' as EndChoice, label: tr('1 day') },
  { key: '7' as EndChoice, label: tr('7 days') },
  { key: '30' as EndChoice, label: tr('30 days') },
  { key: 'date' as EndChoice, label: tr('Pick a date') },
  ...(allowNever ? [{ key: 'never' as EndChoice, label: tr('No end date') }] : []),
];

/** The two looks, each with a small picture of itself. */
function ThemePicker({ value, onChange }: { value: ShareTheme; onChange: (t: ShareTheme) => void }) {
  const opts: { key: ShareTheme; title: string; desc: string; art: ReactNode }[] = [
    {
      key: 'gallery',
      title: tr('Photo gallery'),
      desc: tr('Big pictures in a grid. Best for photos.'),
      art: (
        <svg viewBox="0 0 64 40" aria-hidden className="theme-art">
          <rect x="2" y="2" width="38" height="17" rx="3" /><rect x="43" y="2" width="19" height="17" rx="3" />
          <rect x="2" y="22" width="18" height="16" rx="3" /><rect x="23" y="22" width="18" height="16" rx="3" /><rect x="44" y="22" width="18" height="16" rx="3" />
        </svg>
      ),
    },
    {
      key: 'list',
      title: tr('List of files'),
      desc: tr('Names, sizes and folders. Best for documents.'),
      art: (
        <svg viewBox="0 0 64 40" aria-hidden className="theme-art">
          {[4, 13, 22, 31].map((y) => <g key={y}><rect x="2" y={y} width="6" height="5" rx="1" /><rect x="11" y={y + 1} width={y === 13 ? 30 : 40} height="3" rx="1.5" /></g>)}
        </svg>
      ),
    },
  ];
  return (
    <div className="theme-pick" role="radiogroup" aria-label={tr('How it looks')}>
      {opts.map((o) => (
        <label key={o.key} className={`theme-card ${value === o.key ? 'on' : ''}`}>
          <input type="radio" name="share-theme" checked={value === o.key} onChange={() => onChange(o.key)} />
          {o.art}
          <span className="optcard-title">{o.title}</span>
          <span className="optcard-desc">{o.desc}</span>
        </label>
      ))}
    </div>
  );
}

function PasswordField({ value, onChange, required }: { value: string; onChange: (v: string) => void; required: boolean }) {
  const [show, setShow] = useState(true);
  const short = value.length > 0 && value.length < SHARE_PASSWORD_MIN;
  return (
    <Field
      label={tr('Password')}
      hint={tr('Send the password separately from the link. For example, the link by email and the password in a text message.')}
      error={short ? tr('Use at least {n} characters.', { n: SHARE_PASSWORD_MIN }) : null}
    >
      <div className="row" style={{ flexWrap: 'nowrap' }}>
        <input type={show ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)} autoComplete="new-password" required={required} dir="ltr" style={{ flex: 1 }} />
        <Button type="button" size="sm" variant="ghost" onClick={() => setShow(!show)}>{show ? tr('Hide') : tr('Show')}</Button>
        <Button type="button" size="sm" onClick={() => { onChange(newPassword()); setShow(true); }}>{tr('Make one for me')}</Button>
      </div>
    </Field>
  );
}

/** Ready-to-send: the link, ways to send it with the person's own apps, and the password once. */
function LinkReady({ name, link, password, expiresAt, onDone, shareId }: { name: string; link: string; password: string | null; expiresAt: string | null; shareId: string; onDone: () => void }) {
  const t = useTenant();
  const nav = useNavigate();
  return (
    <Modal
      title={tr('Your link is ready')}
      onClose={onDone}
      footer={
        <>
          <Button onClick={() => nav(`${t.base}/shares/${shareId}`)}>{tr("Open the link's page")}</Button>
          <Button variant="primary" onClick={onDone}>{tr('Done')}</Button>
        </>
      }
    >
      <div className="stack">
        <p style={{ margin: 0 }}>
          {password
            ? tr('Anyone who has this link and its password can see and download these files.')
            : tr('Anyone who has this link can see and download these files.')}{' '}
          {expiresAt ? tr('It works until {date}.', { date: dateOnly(expiresAt) }) : tr('It works until you stop it.')}
        </p>
        <CopyBox text={link} label={tr('Link')} />
        <div className="row">
          <a className="btn sm" href={`mailto:?subject=${encodeURIComponent(name)}&body=${encodeURIComponent(link)}`}>{tr('Send by email')}</a>
          <a className="btn sm" href={`https://wa.me/?text=${encodeURIComponent(link)}`} target="_blank" rel="noopener noreferrer">{tr('Send on WhatsApp')}</a>
        </div>
        {password && (
          <div className="notice">
            <CopyBox text={password} label={tr('Password')} />
            <div className="small" style={{ marginTop: 8 }}>{tr('Send the password separately from the link. It is not shown again, but you can set a new one on the link\'s page.')}</div>
          </div>
        )}
      </div>
    </Modal>
  );
}

/** The share dialog: what is shared, its name, how it looks, until when, and an optional password. */
export function ShareDialog({ source, paths, onClose }: { source: ShareSource; paths: string[]; onClose: () => void }) {
  const t = useTenant();
  const qc = useQueryClient();
  const est = useQuery<Estimate>({ queryKey: ['share-estimate', source, paths], queryFn: () => post(`/api/t/${t.tenantId}/shares/estimate`, { ...source, paths }) });
  const [name, setName] = useState(paths.length === 1 ? lastName(paths[0]!) : '');
  const [theme, setTheme] = useState<ShareTheme | null>(null);
  const [end, setEnd] = useState<EndChoice>(String(SHARE_DEFAULT_END_DAYS) as EndChoice);
  const [date, setDate] = useState(tomorrow());
  const [protect, setProtect] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [made, setMade] = useState<{ id: string; link: string; expiresAt: string | null } | null>(null);
  const e = est.data;
  // Suggest the gallery when most of what is shared is pictures.
  useEffect(() => {
    if (e && theme === null) setTheme(e.files && e.pictures / e.files >= 0.6 ? 'gallery' : 'list');
  }, [e, theme]);
  useEffect(() => {
    if (e?.passwordRequired) setProtect(true);
  }, [e?.passwordRequired]);

  if (made) return <LinkReady name={name} link={made.link} password={protect ? password : null} expiresAt={made.expiresAt} shareId={made.id} onDone={onClose} />;

  const tooBig = !!e && (e.bytes > e.maxBytes || e.files > e.maxFiles);
  const pwOk = !protect || password.length >= SHARE_PASSWORD_MIN;
  const canMake = !!e && e.sharingEnabled && e.files > 0 && !tooBig && name.trim().length > 0 && pwOk && !!theme;
  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const ends = endOf(end, date);
      const r = await post(`/api/t/${t.tenantId}/shares`, { ...source, paths, name: name.trim(), theme, password: protect ? password : null, ...ends });
      const expiresAt = ends.endsInDays ? new Date(Date.now() + ends.endsInDays * 86_400_000).toISOString() : (ends.expiresAt ?? null);
      setMade({ id: r.id, link: r.link, expiresAt });
      qc.invalidateQueries({ queryKey: ['shares'] });
    } catch (x) {
      setErr(friendlyError(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={tr('Share files')}
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>{tr('Cancel')}</Button>
          <Button variant="primary" disabled={!canMake} busy={busy} onClick={submit}>{tr('Create link')}</Button>
        </>
      }
    >
      {est.isLoading ? <Loading rows={3} /> : est.error ? <ErrorBox error={est.error} /> : e && (
        <div className="stack">
          {!e.sharingEnabled && <div className="notice warn">{tr('Shared links are turned off for this organization. An admin can turn them on in Settings.')}</div>}
          <div className="notice">
            <strong>{tr('{files} files · {size}', { files: e.files.toLocaleString(locale()), size: bytes(e.bytes) })}</strong>
            {e.pictures > 0 && <span className="muted"> · {tr('{n} pictures', { n: e.pictures.toLocaleString(locale()) })}</span>}
            {tooBig && <div className="small" style={{ marginTop: 4 }}>{tr('A shared link can hold up to {size} and {files} files. Choose fewer.', { size: bytes(e.maxBytes), files: e.maxFiles.toLocaleString(locale()) })}</div>}
            {e.files === 0 && <div className="small" style={{ marginTop: 4 }}>{tr('There are no files to share in this selection.')}</div>}
          </div>
          <Field label={tr('Name')} hint={tr('The person who opens the link sees this as the title.')}>
            <input type="text" value={name} maxLength={120} onChange={(x) => setName(x.target.value)} placeholder={tr('For example: Wedding photos')} autoFocus />
          </Field>
          <Field label={tr('How it looks')}>
            <ThemePicker value={theme ?? 'list'} onChange={setTheme} />
          </Field>
          <Field
            label={tr('The link works for')}
            hint={source.folderArchiveId && e.filesRemovedAt
              ? tr('Some of these files are kept in the cloud only until {date}. The link stops showing them then.', { date: dateOnly(e.filesRemovedAt) })
              : tr('Ending the link does not delete any files. If your backup plan removes this backup, the link stops working.')}
          >
            <Segmented options={END_OPTIONS(true)} value={end} onChange={setEnd} />
            {end === 'date' && <input type="date" value={date} min={tomorrow()} onChange={(x) => setDate(x.target.value)} style={{ marginTop: 8, maxWidth: 220 }} />}
          </Field>
          <label className="check">
            <input type="checkbox" checked={protect} disabled={e.passwordRequired} onChange={(x) => setProtect(x.target.checked)} />
            <span>
              {tr('Protect with a password')}
              {e.passwordRequired && <span className="hint" style={{ display: 'block' }}>{tr('Your organization asks for a password on every shared link.')}</span>}
            </span>
          </label>
          {protect && <PasswordField value={password} onChange={setPassword} required />}
          {err && <div className="notice bad">{err}</div>}
        </div>
      )}
    </Modal>
  );
}

/** A selection bar for browsers that share: "3 chosen · Share · Cancel". */
export function ShareBar({ count, onShare, onCancel, extra }: { count: number; onShare: () => void; onCancel: () => void; extra?: ReactNode }) {
  return (
    <div className="notice row between">
      <span>
        <strong>{count === 0 ? tr('Choose the files and folders to share') : count === 1 ? tr('1 item chosen') : tr('{n} items chosen', { n: count })}</strong>
      </span>
      <span className="row">
        {extra}
        <Button onClick={onCancel}>{tr('Cancel')}</Button>
        <Button variant="primary" disabled={count === 0} onClick={onShare}><Icons.share />{tr('Share')}</Button>
      </span>
    </div>
  );
}

// ───────────────────────────── the list ─────────────────────────────

const END_REASON: Record<string, string> = {
  expired: msg('Its end date passed'),
  source_gone: msg('Its files were removed from the backup'),
  creator_removed: msg('The person who made it left the organization'),
  device_retired: msg('Its computer was removed'),
  sharing_disabled: msg('Shared links were turned off'),
  stopped_by_operator: msg('Stopped by ZeroGap support'),
};

export function ShareStatus({ s }: { s: Pick<ShareView, 'status' | 'expiresAt'> }) {
  if (s.status === 'stopped') return <Badge tone="neutral">{tr('Stopped')}</Badge>;
  if (s.status === 'ended') return <Badge tone="neutral">{tr('Ended')}</Badge>;
  const soon = s.expiresAt && Date.parse(s.expiresAt) - Date.now() < 3 * 86_400_000;
  return soon ? <Badge tone="warn">{tr('Ends {time}', { time: ago(s.expiresAt) })}</Badge> : <Badge tone="ok">{tr('Active')}</Badge>;
}

function fromText(s: ShareView) {
  const where = s.archivePath ? tr('Archive of {folder}', { folder: lastName(s.archivePath) }) : s.backupTime ? tr('Backup of {date}', { date: when(s.backupTime) }) : '';
  return [s.deviceName, where].filter(Boolean).join(' · ');
}

export function SharedLinks() {
  const t = useTenant();
  const [show, setShow] = useState<'active' | 'all'>('active');
  const q = useQuery({ queryKey: ['shares', t.tenantId, show], queryFn: () => get(`/api/t/${t.tenantId}/shares${qs({ status: show })}`) });
  const list: ShareView[] = q.data?.shares ?? [];
  return (
    <div>
      <PageHead
        title={tr('Shared links')}
        desc={tr('Links that let people outside your organization see and download files from your backups. Every time someone opens one or downloads a file, it is recorded.')}
      />
      <div className="row" style={{ marginBottom: 12 }}>
        <Segmented options={[{ key: 'active', label: tr('Working now') }, { key: 'all', label: tr('All') }]} value={show} onChange={setShow} />
      </div>
      <div className="card table-wrap">
        {q.isLoading ? <div className="card-pad"><Loading /></div> : q.error ? <div className="card-pad"><ErrorBox error={q.error} /></div> : list.length === 0 ? (
          <Empty glyph="↗" title={show === 'active' ? tr('No links are working right now') : tr('No shared links yet')}>
            {tr('To share files, open a computer’s backups or an archive, choose the files, and press Share.')}
          </Empty>
        ) : (
          <table>
            <thead><tr><th>{tr('Name')}</th><th>{tr('From')}</th><th>{tr('Made by')}</th><th>{tr('Status')}</th><th className="right">{tr('Opened')}</th></tr></thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.id}>
                  <td>
                    <Link to={`${t.base}/shares/${s.id}`}><strong><bdi>{s.name}</bdi></strong></Link>
                    <div className="small muted">{tr('{files} files · {size}', { files: s.files.toLocaleString(locale()), size: bytes(s.bytes) })}{s.hasPassword ? ` · ${tr('password')}` : ''}</div>
                  </td>
                  <td className="small"><bdi>{fromText(s)}</bdi></td>
                  <td className="small">{s.createdBy ?? tr('Someone who left')}<div className="muted">{ago(s.createdAt)}</div></td>
                  <td><ShareStatus s={s} />{s.status === 'active' && s.expiresAt && <div className="small muted">{tr('until {date}', { date: dateOnly(s.expiresAt) })}</div>}</td>
                  <td className="right small num">{s.opens.toLocaleString(locale())}{s.lastOpenedAt && <div className="muted">{ago(s.lastOpenedAt)}</div>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ───────────────────────────── one link ─────────────────────────────

/** "Chrome on Android" from a browser's self-description; good enough to tell visits apart. */
export function deviceOf(ua: string | null | undefined): string {
  if (!ua) return tr('Unknown browser');
  const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /SamsungBrowser/.test(ua) ? 'Samsung Internet' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : null;
  const os = /Android/.test(ua) ? 'Android' : /iPhone/.test(ua) ? 'iPhone' : /iPad/.test(ua) ? 'iPad' : /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : null;
  if (browser && os) return tr('{browser} on {os}', { browser, os });
  return browser ?? os ?? tr('Unknown browser');
}

function activityText(e: { action: string; details: any; actor: string | null }): ReactNode {
  const who = e.actor ?? tr('Someone');
  switch (e.action) {
    case 'share.created': return tr('{who} made the link', { who });
    case 'share.updated': return tr('{who} changed the link', { who });
    case 'share.link_copied': return tr('{who} copied the link', { who });
    case 'share.stopped': return e.actor ? tr('{who} stopped the link', { who }) : tr('ZeroGap support stopped the link');
    case 'share.ended': return tr(END_REASON[e.details?.reason] ?? msg('The link ended'));
    case 'share.opened': return tr('Opened the link');
    case 'share.password_failed': return tr('Typed a wrong password');
    case 'share.locked': return tr('Locked out for 15 minutes after too many wrong passwords');
    case 'share.denied': return tr('Tried the link after it stopped working');
    case 'share.file_downloaded': return <>{tr('Downloaded|a file')} <bdi className="mono small">{e.details?.path}</bdi></>;
    case 'share.zip_downloaded': return e.details?.folder
      ? <>{tr('Downloaded the folder')} <bdi className="mono small">{e.details.folder}</bdi> ({tr('{n} files', { n: e.details.files })})</>
      : tr('Downloaded everything ({n} files)', { n: e.details?.files ?? 0 });
    default: return e.action;
  }
}

function ChangeDialog({ s, passwordRequired, onClose, onSaved }: { s: ShareView; passwordRequired: boolean; onClose: () => void; onSaved: (password: string | null) => void }) {
  const t = useTenant();
  const [name, setName] = useState(s.name);
  const [theme, setTheme] = useState<ShareTheme>(s.theme);
  const [end, setEnd] = useState<EndChoice | 'keep'>('keep');
  const [date, setDate] = useState(s.expiresAt ? s.expiresAt.slice(0, 10) : tomorrow());
  const [pw, setPw] = useState<'keep' | 'new' | 'remove'>(s.hasPassword ? 'keep' : passwordRequired ? 'new' : 'keep');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const body: Record<string, unknown> = {};
      if (name.trim() !== s.name) body.name = name.trim();
      if (theme !== s.theme) body.theme = theme;
      if (end !== 'keep') Object.assign(body, endOf(end, date));
      if (pw === 'new') body.password = password;
      if (pw === 'remove') body.password = null;
      await patch(`/api/t/${t.tenantId}/shares/${s.id}`, body);
      onSaved(pw === 'new' ? password : null);
    } catch (x) {
      setErr(friendlyError(x));
    } finally {
      setBusy(false);
    }
  };
  const pwOptions = [
    { key: 'keep' as const, label: s.hasPassword ? tr('Keep the password') : tr('No password') },
    { key: 'new' as const, label: s.hasPassword ? tr('New password') : tr('Add a password') },
    ...(s.hasPassword && !passwordRequired ? [{ key: 'remove' as const, label: tr('Remove the password') }] : []),
  ];
  return (
    <Modal
      title={tr('Change the link')}
      onClose={onClose}
      wide
      footer={<><Button onClick={onClose}>{tr('Cancel')}</Button><Button variant="primary" busy={busy} disabled={!name.trim() || (pw === 'new' && password.length < SHARE_PASSWORD_MIN)} onClick={save}>{tr('Save')}</Button></>}
    >
      <div className="stack">
        <Field label={tr('Name')}><input type="text" value={name} maxLength={120} onChange={(x) => setName(x.target.value)} /></Field>
        <Field label={tr('How it looks')}><ThemePicker value={theme} onChange={setTheme} /></Field>
        <Field label={tr('The link works')} hint={s.expiresAt ? tr('Now: until {date}.', { date: dateOnly(s.expiresAt) }) : tr('Now: until you stop it.')}>
          <Segmented options={[{ key: 'keep' as const, label: tr('As it is') }, ...END_OPTIONS(true)]} value={end} onChange={setEnd} />
          {end === 'date' && <input type="date" value={date} min={tomorrow()} onChange={(x) => setDate(x.target.value)} style={{ marginTop: 8, maxWidth: 220 }} />}
        </Field>
        <Field label={tr('Password')} hint={pw === 'new' && s.hasPassword ? tr('People who already opened the link will need the new password.') : undefined}>
          <Segmented options={pwOptions} value={pw} onChange={setPw} />
        </Field>
        {pw === 'new' && <PasswordField value={password} onChange={setPassword} required />}
        {err && <div className="notice bad">{err}</div>}
      </div>
    </Modal>
  );
}

function ShareFiles({ shareId }: { shareId: string }) {
  const t = useTenant();
  const [dir, setDir] = useState('');
  const page = useInfiniteQuery({
    queryKey: ['share-items', shareId, dir],
    queryFn: ({ pageParam }) => get(`/api/t/${t.tenantId}/shares/${shareId}/items${qs({ dir, offset: pageParam, limit: 200 })}`),
    initialPageParam: 0,
    getNextPageParam: (last: { more: boolean }, all) => (last.more ? all.length * 200 : undefined),
  });
  const rows = page.data?.pages.flatMap((p: any) => p.items) ?? [];
  const crumbs = dir ? dir.split('/') : [];
  return (
    <div className="card">
      <div className="card-head">
        <div className="crumbs">
          <button onClick={() => setDir('')}>{tr('Shared files')}</button>
          {crumbs.map((c, i) => (
            <span key={i} className="row" style={{ gap: 4 }}>
              <span className="muted">{arrow.crumb()}</span>
              <button onClick={() => setDir(crumbs.slice(0, i + 1).join('/'))}><bdi>{c}</bdi></button>
            </span>
          ))}
        </div>
      </div>
      <div className="table-wrap" style={{ maxHeight: 420, overflow: 'auto' }}>
        <table>
          <tbody>
            {rows.map((e: any) => (
              <tr key={e.id}>
                <td>
                  <div className="fname">
                    {e.kind === 'd' ? <Icons.folder className="ficon dir" /> : <Icons.file className="ficon" />}
                    {e.kind === 'd'
                      ? <button className="btn ghost sm" style={{ padding: '2px 4px' }} onClick={() => setDir(e.path)}><bdi>{e.name}</bdi></button>
                      : <bdi>{e.name}</bdi>}
                  </div>
                </td>
                <td className="right num small">{e.kind === 'd' ? '' : bytes(e.size)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {page.isLoading && <div className="card-pad"><Loading rows={3} /></div>}
        {!page.isLoading && rows.length === 0 && <Empty glyph="∅" title={tr('Nothing here')} />}
        {page.hasNextPage && <div className="card-pad"><Button onClick={() => page.fetchNextPage()} busy={page.isFetchingNextPage}>{tr('Show more')}</Button></div>}
      </div>
    </div>
  );
}

export function SharedLinkPage() {
  const t = useTenant();
  const { shareId } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['shares', t.tenantId, shareId], queryFn: () => get(`/api/t/${t.tenantId}/shares/${shareId}`) });
  const tenant = useQuery({ queryKey: ['tenant', t.tenantId], queryFn: () => get(`/api/t/${t.tenantId}`) });
  const activity = useQuery({ queryKey: ['share-activity', shareId], queryFn: () => get(`/api/t/${t.tenantId}/shares/${shareId}/activity`) });
  const [changing, setChanging] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [newPw, setNewPw] = useState<string | null>(null);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const s: ShareView = q.data.share;
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['shares'] });
    qc.invalidateQueries({ queryKey: ['share-activity', shareId] });
  };
  const copy = async () => {
    try {
      const r = await get(`/api/t/${t.tenantId}/shares/${s.id}/link`);
      await navigator.clipboard.writeText(r.link);
      toast(tr('Link copied'));
      qc.invalidateQueries({ queryKey: ['share-activity', shareId] });
    } catch (e) {
      toast(friendlyError(e), true);
    }
  };
  const visits = (activity.data?.events ?? []).filter((e: any) => e.actorType === 'anonymous');
  const changes = (activity.data?.events ?? []).filter((e: any) => e.actorType !== 'anonymous');
  return (
    <div>
      <PageHead
        eyebrow={<Link to={`${t.base}/shares`}>{arrow.back()} {tr('Shared links')}</Link>}
        title={<bdi>{s.name}</bdi>}
        desc={<ShareStatus s={s} />}
        actions={s.status === 'active' && (
          <>
            <Button variant="primary" onClick={copy}>{tr('Copy link')}</Button>
            <Button onClick={() => setChanging(true)}>{tr('Change')}</Button>
            <Button variant="danger" onClick={() => setStopping(true)}>{tr('Stop sharing')}</Button>
          </>
        )}
      />
      {s.status !== 'active' && (
        <div className="notice" style={{ marginBottom: 16 }}>
          <strong>{tr('This link no longer works.')}</strong>{' '}
          {s.status === 'stopped' ? tr('It was stopped {time}.', { time: ago(s.stoppedAt) }) : tr(END_REASON[s.endReason ?? ''] ?? msg('The link ended'))}{' '}
          {tr('The files themselves were not touched.')}
        </div>
      )}
      {newPw && (
        <div className="notice" style={{ marginBottom: 16 }}>
          <CopyBox text={newPw} label={tr('New password')} />
          <div className="small" style={{ marginTop: 8 }}>{tr('Send the password separately from the link. It is not shown again.')}</div>
        </div>
      )}
      <div className="grid g2">
        <Card title={tr('About this link')}>
          <dl className="kv">
            <dt>{tr('Shared from')}</dt><dd><Link to={`${t.base}/devices/${s.deviceId}`}><bdi>{fromText(s)}</bdi></Link></dd>
            <dt>{tr('Files')}</dt><dd className="num">{tr('{files} files · {size}', { files: s.files.toLocaleString(locale()), size: bytes(s.bytes) })}</dd>
            <dt>{tr('How it looks')}</dt><dd>{s.theme === 'gallery' ? tr('Photo gallery') : tr('List of files')}</dd>
            <dt>{tr('Works until')}</dt><dd>{s.expiresAt ? dateOnly(s.expiresAt) : tr('You stop it')}</dd>
            <dt>{tr('Password')}</dt><dd>{s.hasPassword ? tr('Yes') : tr('No')}</dd>
            <dt>{tr('Made by')}</dt><dd>{s.createdBy ?? tr('Someone who left')} · {when(s.createdAt)}</dd>
          </dl>
        </Card>
        <Card title={tr('Who opened it')}>
          <div className="row" style={{ gap: 24, marginBottom: 12 }}>
            <div><div className="num" style={{ fontSize: 24 }}>{s.opens.toLocaleString(locale())}</div><div className="small muted">{tr('times opened')}</div></div>
            <div><div className="num" style={{ fontSize: 24 }}>{s.downloads.toLocaleString(locale())}</div><div className="small muted">{tr('downloads')}</div></div>
          </div>
          {activity.isLoading ? <Loading rows={2} /> : visits.length === 0 ? <p className="muted small" style={{ margin: 0 }}>{tr('Nobody has opened it yet.')}</p> : (
            <ul className="timeline">
              {visits.slice(0, 50).map((e: any) => (
                <li key={e.id}>
                  <div>{activityText(e)}{e.outcome !== 'success' && <> <Badge tone="warn" dot={false}>{e.outcome === 'denied' ? tr('blocked') : tr('failed')}</Badge></>}</div>
                  <div className="small muted">
                    {when(e.at)} · {deviceOf(e.userAgent)}
                    <TechDetails inline summary={tr('Address')}><span className="mono small" dir="ltr">{e.ip}</span><div className="mono small" dir="ltr">{e.userAgent}</div></TechDetails>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
      <div className="grid g2" style={{ marginTop: 20 }}>
        <ShareFiles shareId={s.id} />
        <Card title={tr('Changes')}>
          <ul className="timeline">
            {changes.map((e: any) => (
              <li key={e.id}>
                <div>{activityText(e)}{e.details?.via === 'assistant' && <> <Badge tone="info" dot={false}>{tr('with the assistant')}</Badge></>}</div>
                <div className="small muted">{when(e.at)}</div>
              </li>
            ))}
          </ul>
        </Card>
      </div>
      {changing && (
        <ChangeDialog
          s={s}
          passwordRequired={tenant.data?.tenant?.settings?.sharePasswordRequired === true}
          onClose={() => setChanging(false)}
          onSaved={(pw) => { setChanging(false); setNewPw(pw); refresh(); toast(tr('Saved')); }}
        />
      )}
      {stopping && (
        <ConfirmDialog
          title={tr('Stop sharing?')}
          confirm={tr('Stop sharing')}
          danger
          onClose={() => setStopping(false)}
          onConfirm={async () => {
            await post(`/api/t/${t.tenantId}/shares/${s.id}/stop`);
            setStopping(false);
            refresh();
          }}
        >
          <p style={{ margin: 0 }}>{tr('The link stops working right away, also for anyone who has it open now. The files themselves are not touched. This cannot be undone, but you can make a new link.')}</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

// ───────────────────────────── settings ─────────────────────────────

export function SharingSettings({ settings, manage }: { settings: Record<string, unknown> | undefined; manage: boolean }) {
  const t = useTenant();
  const qc = useQueryClient();
  const toast = useToast();
  const enabled = settings?.sharingEnabled !== false;
  const required = settings?.sharePasswordRequired === true;
  const [confirmOff, setConfirmOff] = useState(false);
  const active = useQuery({ queryKey: ['shares', t.tenantId, 'active'], queryFn: () => get(`/api/t/${t.tenantId}/shares${qs({ status: 'active' })}`), enabled: manage && confirmOff });
  const save = async (body: Record<string, boolean>) => {
    try {
      await patch(`/api/t/${t.tenantId}/settings`, body);
      toast(tr('Saved'));
      qc.invalidateQueries({ queryKey: ['tenant'] });
      qc.invalidateQueries({ queryKey: ['shares'] });
    } catch (e) {
      toast(friendlyError(e), true);
    }
  };
  return (
    <Card title={tr('Shared links')}>
      <fieldset disabled={!manage} className="plain-fieldset">
        <label className="check" style={{ marginBottom: 14 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => (e.target.checked ? save({ sharingEnabled: true }) : setConfirmOff(true))} />
          <span>
            {tr('Allow shared links')}
            <span className="hint" style={{ display: 'block' }}>{tr('Admins can make links that let people outside the organization see and download chosen files.')}</span>
          </span>
        </label>
        <label className="check">
          <input type="checkbox" checked={required} disabled={!enabled} onChange={(e) => save({ sharePasswordRequired: e.target.checked })} />
          <span>{tr('Always require a password')}</span>
        </label>
      </fieldset>
      {confirmOff && (
        <ConfirmDialog title={tr('Turn off shared links?')} confirm={tr('Turn off')} danger onClose={() => setConfirmOff(false)} onConfirm={async () => { await save({ sharingEnabled: false }); setConfirmOff(false); }}>
          <p style={{ margin: 0 }}>
            {active.data
              ? tr('{n} links that work now will stop for good. Turning sharing back on does not bring them back.', { n: active.data.shares.length })
              : tr('Every link that works now will stop for good. Turning sharing back on does not bring them back.')}
          </p>
        </ConfirmDialog>
      )}
    </Card>
  );
}
